import expandRecurringOccurrences from '../availabilityHelpers/expandRecurringOccurrences'
import { intervalsOverlap } from '../availabilityHelpers/findAbsence'
import { isAfterLastDay } from './agendaSchedulePersonas'

// AI-CONTEXT: See docs/features/agenda-persoonskleur-en-huisbezoek-titel.md
//
// Een roosterblok en een huisbezoek van dezelfde persona stonden in de agenda
// over elkaar heen. Dit knipt het bezoek WEERGAVE-gewijs uit het roosterblok:
// de roostertaak zelf en de aanwezigheidsregistratie die 's nachts uit dat
// rooster wordt geschreven blijven onaangeroerd — een `exdate` op de taak zou
// de aanwezigheid van die dag weghalen terwijl de medewerker juist werkt.
//
// Per occurrence, niet per taakdocument: een wekelijks rooster is één document
// met veel dagen, en alleen de dag mét een bezoek mag anders worden.
// AI-CONTEXT: See docs/pitfalls.md#state-recurring-event-wrong-date

/**
 * @typedef {object} ScheduleSegment
 * @property {string} docid - Het `Tasks`-document van het roosterblok
 * @property {string} personaId - Wiens rooster dit is
 * @property {Date} start - Begin van het overgebleven stuk
 * @property {Date} end - Einde van het overgebleven stuk
 * @property {Date} occurrenceStart - Begin van de oorspronkelijke occurrence (voor klik en verwijderen)
 */

/**
 * @typedef {object} SplitResult
 * @property {Record<string, Array<Date>>} hiddenOccurrences - Per docid de occurrence-starts die niet als geheel getoond mogen worden
 * @property {Array<ScheduleSegment>} segments - De stukken die ervoor in de plaats komen
 */

const hasEnd = (task) => task?.TimestampEnd && typeof task.TimestampEnd.toDate === 'function'

/**
 * Trekt de (gesorteerde, mogelijk overlappende) gaten van één occurrence af.
 *
 * @param {{start: Date, end: Date}} occurrence
 * @param {Array<{start: Date, end: Date}>} gaps
 * @returns {Array<{start: Date, end: Date}>}
 */
const subtractGaps = (occurrence, gaps) => {
    const pieces = []
    let cursor = occurrence.start

    gaps.forEach((gap) => {
        if (cursor >= occurrence.end) return
        if (gap.start > cursor) {
            pieces.push({ start: cursor, end: gap.start < occurrence.end ? gap.start : occurrence.end })
        }
        if (gap.end > cursor) cursor = gap.end
    })

    if (cursor < occurrence.end) pieces.push({ start: cursor, end: occurrence.end })
    return pieces
}

/**
 * @param {object} args
 * @param {Array<object>} args.schedules - Taken van het soort `personaSchedule`
 * @param {Array<object>} args.visits - Alle overige taken die in beeld staan (huisbezoeken en andere)
 * @param {Date|null} args.windowStart - Begin van de zichtbare periode
 * @param {Date|null} args.windowEnd - Einde van de zichtbare periode
 * @param {Record<string, Date>} [args.scheduleEndByPersonaId] - Laatste roosterdag per persona (uitstroomdatum); daarna is er geen blok om te knippen
 * @returns {SplitResult}
 */
const splitScheduleAroundVisits = ({ schedules, visits, windowStart, windowEnd, scheduleEndByPersonaId = {} }) => {
    /** @type {SplitResult} */
    const empty = { hiddenOccurrences: {}, segments: [] }
    if (!(windowStart instanceof Date) || !(windowEnd instanceof Date)) return empty
    if (!Array.isArray(schedules) || !Array.isArray(visits)) return empty

    // Bezoek-intervallen per persona. Een bezoek zonder eindtijd is een
    // "hele dag"-markering zonder tijdvak; die knipt niets.
    /** @type {Record<string, Array<{start: Date, end: Date}>>} */
    const gapsByPersona = {}
    visits.forEach((visit) => {
        if (!visit || visit.Type === 'personaSchedule' || !hasEnd(visit)) return
        const members = Array.isArray(visit.TeamMembers) ? visit.TeamMembers : []
        if (members.length === 0) return

        const occurrences = expandRecurringOccurrences(visit, windowStart, windowEnd)
            .filter((occ) => occ.end > occ.start)
        if (occurrences.length === 0) return

        members.forEach((personaId) => {
            if (!gapsByPersona[personaId]) gapsByPersona[personaId] = []
            gapsByPersona[personaId].push(...occurrences)
        })
    })
    Object.values(gapsByPersona).forEach((gaps) => gaps.sort((a, b) => a.start - b.start))

    /** @type {SplitResult} */
    const result = { hiddenOccurrences: {}, segments: [] }

    schedules.forEach((schedule) => {
        if (!schedule || schedule.Type !== 'personaSchedule' || !schedule.docid) return
        const gaps = gapsByPersona[schedule.PersonaID]
        if (!gaps) return

        const lastDay = scheduleEndByPersonaId[schedule.PersonaID]

        expandRecurringOccurrences(schedule, windowStart, windowEnd).forEach((occurrence) => {
            if (isAfterLastDay(occurrence.start, lastDay)) return
            const overlapping = gaps.filter((gap) =>
                intervalsOverlap(occurrence.start, occurrence.end, gap.start, gap.end)
            )
            if (overlapping.length === 0) return

            if (!result.hiddenOccurrences[schedule.docid]) result.hiddenOccurrences[schedule.docid] = []
            result.hiddenOccurrences[schedule.docid].push(occurrence.start)

            subtractGaps(occurrence, overlapping).forEach((piece) => {
                result.segments.push({
                    docid: schedule.docid,
                    personaId: schedule.PersonaID,
                    start: piece.start,
                    end: piece.end,
                    occurrenceStart: occurrence.start,
                })
            })
        })
    })

    return result
}

export default splitScheduleAroundVisits
